import { useState } from 'react';
import { DataTable, ErrorState, Modal, Pager, type DataTableColumn } from '../../components';
import { formatDate } from '../../lib/format/dates';
import { formatCount } from '../../lib/format/money';
import { ChefDialog, type ChefTab } from '../chefs/ChefDialog';
import { EaterDialog, type EaterTab } from '../eaters/EaterDialog';
import type { AdminProfileRow } from '../profiles/api';
import { RatingCell } from '../profiles/people';
import { useCampaignPeople } from './queries';

/** A person who has published a dish is a chef; anyone else is an eater. */
const isChef = (r: AdminProfileRow) => r.totalPublished > 0;

const COLUMNS: DataTableColumn<AdminProfileRow>[] = [
  {
    id: 'email',
    header: 'Email',
    width: '2fr',
    cell: (r) => <span className="truncate text-[13px]">{r.email || '—'}</span>,
  },
  {
    id: 'joined',
    header: 'Joined',
    width: '1fr',
    cell: (r) => <span className="text-muted">{formatDate(r.joinedAt)}</span>,
  },
  {
    id: 'orders',
    header: 'Orders',
    width: '90px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.orderCount),
  },
  {
    id: 'dishes',
    header: 'Dishes',
    width: '90px',
    align: 'right',
    numeric: true,
    cell: (r) => formatCount(r.totalPublished),
  },
  {
    id: 'rating',
    header: 'Rating',
    width: '92px',
    align: 'center',
    // As on the Chefs and Eaters lists: a chef's rating from eaters, an eater's from chefs.
    cell: (r) => <RatingCell rating={isChef(r) ? r.avgRating : r.eaterAvgRating} />,
  },
];

type ListKind = 'registered' | 'existing';
const TITLE: Record<ListKind, string> = {
  registered: 'Registered people',
  existing: 'Already signed up when they came through',
};

/** A count that opens its list — plain text when there is nobody to list. */
function CountLink({ n, label, onOpen }: { n: number; label: string; onOpen: () => void }) {
  if (n <= 0) return <>{formatCount(n)}</>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${formatCount(n)} ${label} — show the list`}
      className="cursor-pointer border-0 bg-transparent p-0 font-body font-semibold tabular-nums text-brown underline underline-offset-2 hover:text-orange"
    >
      {formatCount(n)}
    </button>
  );
}

/**
 * The campaign's "People" figure: registered / unregistered / existing. The
 * first and third numbers open their lists; a person opens their chef or
 * eater dialog, and closing that returns to the list.
 */
export function CampaignPeople({
  campaignId,
  registered,
  unregistered,
  existing,
}: {
  campaignId: string;
  registered: number;
  unregistered: number;
  existing: number;
}) {
  const [open, setOpen] = useState<ListKind | null>(null);
  const [person, setPerson] = useState<{ id: string; chef: boolean } | null>(null);

  return (
    <>
      <CountLink n={registered} label="registered people" onOpen={() => setOpen('registered')} /> /{' '}
      {formatCount(unregistered)} /{' '}
      <CountLink n={existing} label="already signed up" onOpen={() => setOpen('existing')} />
      {open && !person && (
        <PeopleDialog
          campaignId={campaignId}
          kind={open}
          onClose={() => setOpen(null)}
          onPick={(r) => setPerson({ id: r.profileId, chef: isChef(r) })}
        />
      )}
      {person && <PersonDialog id={person.id} chef={person.chef} onClose={() => setPerson(null)} />}
    </>
  );
}

function PeopleDialog({
  campaignId,
  kind,
  onClose,
  onPick,
}: {
  campaignId: string;
  kind: ListKind;
  onClose: () => void;
  onPick: (r: AdminProfileRow) => void;
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const q = useCampaignPeople(campaignId, { page, pageSize }, kind === 'existing');

  return (
    <Modal isOpen onClose={onClose} size="2xl" title={TITLE[kind]}>
      {q.error && <ErrorState error={q.error} onRetry={() => void q.refetch()} compact />}
      <DataTable
        label={TITLE[kind]}
        compact
        minWidth={700}
        columns={COLUMNS}
        rows={q.data?.items ?? []}
        getRowId={(r) => r.profileId}
        onRowClick={onPick}
        loading={q.isPending}
        emptyLabel="Nobody yet."
      />
      <Pager
        page={page}
        pageSize={pageSize}
        total={q.data?.total ?? 0}
        onPageChange={setPage}
        onPageSizeChange={(n) => {
          setPageSize(n);
          setPage(1);
        }}
        noun="people"
      />
    </Modal>
  );
}

function PersonDialog({ id, chef, onClose }: { id: string; chef: boolean; onClose: () => void }) {
  const [chefTab, setChefTab] = useState<ChefTab>('profile');
  const [eaterTab, setEaterTab] = useState<EaterTab>('profile');
  return chef ? (
    <ChefDialog id={id} tab={chefTab} onTab={setChefTab} onClose={onClose} />
  ) : (
    <EaterDialog id={id} tab={eaterTab} onTab={setEaterTab} onClose={onClose} />
  );
}
